import React from 'react';
import { Button, Collapse, InputNumber } from 'antd';
import { AiFillCaretRight } from 'react-icons/ai';
import { CheckboxFilterList, CheckboxFilterListOption } from '../../../../shared/components/CheckboxFilterList';
import { SingleDateFilterInput } from '../../../../shared/components/SingleDateFilterInput';
import { ExpirationFilterOption, StorageFilterOption, SubscriptionTier } from '../../../../enums/clientEnums';
import type { ClientFilterSelection } from '../../../../types/ClientManagementTypes';
import styles from './FilterPanel.module.scss';

export interface FilterPanelProps {
  selection: ClientFilterSelection;
  onChange: (partial: Partial<ClientFilterSelection>) => void;
  onApply: () => void;
  onClearAll: () => void;
  organisationOptions?: CheckboxFilterListOption[];
  subscriptionOptions?: CheckboxFilterListOption[];
  addOnOptions?: CheckboxFilterListOption[];
}

const { Panel } = Collapse;

// A styled span, not a plain string, so header text stays white regardless of antd's cascade.
const panelHeader = (label: string) => <span className={styles.panelHeaderText}>{label}</span>;

// Only a panel that already holds a value starts open; the rest stay collapsed.
const computeDefaultActiveKeys = (selection: ClientFilterSelection): string[] => {
  const keys: string[] = [];
  if (selection.organisations.length) keys.push('organisation');
  if (selection.subscriptions.length) keys.push('subscription');
  if (selection.addOns.length) keys.push('addOns');
  if (selection.expirationStatuses.length || selection.expirationDate) keys.push('expiration');
  if (selection.storageMetrics.length || selection.storageRange[0] || selection.storageRange[1]) keys.push('storage');
  return keys;
};

export const FilterPanel: React.FC<FilterPanelProps> = ({
  selection,
  onChange,
  onApply,
  onClearAll,
  organisationOptions = [],
  subscriptionOptions = [],
  addOnOptions = [],
}) => {
  const handleStorageFromChange = (from: number | null) => onChange({ storageRange: [from == null ? '' : String(from), selection.storageRange[1]] });
  const handleStorageToChange = (to: number | null) => onChange({ storageRange: [selection.storageRange[0], to == null ? '' : String(to)] });

  return (
    <div className={styles.filterPanel}>
      <Collapse ghost className={styles.accordion} defaultActiveKey={computeDefaultActiveKeys(selection)}>
        <Panel header={panelHeader('ORGANIZATION')} key="organisation" showArrow={false}>
          <CheckboxFilterList
            options={organisationOptions}
            selectedValues={selection.organisations}
            onChange={(organisations) => onChange({ organisations })}
          />
        </Panel>

        <Panel header={panelHeader('SUBSCRIPTION')} key="subscription" showArrow={false}>
          <CheckboxFilterList
            options={subscriptionOptions}
            selectedValues={selection.subscriptions}
            onChange={(subscriptions) => onChange({ subscriptions: subscriptions as SubscriptionTier[] })}
          />
        </Panel>

        <Panel header={panelHeader('ADD-ONS')} key="addOns" showArrow={false}>
          <CheckboxFilterList options={addOnOptions} selectedValues={selection.addOns} onChange={(addOns) => onChange({ addOns })} />
        </Panel>

        <Panel header={panelHeader('EXPIRATION')} key="expiration" showArrow={false}>
          <SingleDateFilterInput
            label="EXPIRES"
            value={selection.expirationDate}
            onChange={(expirationDate) => onChange({ expirationDate })}
          />
          <CheckboxFilterList
            options={[
              { label: 'ACTIVE', value: ExpirationFilterOption.ACTIVE },
              { label: 'EXPIRED', value: ExpirationFilterOption.EXPIRED },
            ]}
            selectedValues={selection.expirationStatuses}
            onChange={(expirationStatuses) => onChange({ expirationStatuses: expirationStatuses as ExpirationFilterOption[] })}
          />
        </Panel>

        <Panel header={panelHeader('STORAGE')} key="storage" showArrow={false}>
          <div className={styles.storageRange}>
            <InputNumber
              className={styles.storageInput}
              min={0}
              controls={false}
              value={selection.storageRange[0] === '' ? null : Number(selection.storageRange[0])}
              onChange={handleStorageFromChange}
            />
            <span className={styles.storageRangeLabel}>TO</span>
            <InputNumber
              className={styles.storageInput}
              min={0}
              controls={false}
              value={selection.storageRange[1] === '' ? null : Number(selection.storageRange[1])}
              onChange={handleStorageToChange}
            />
            <span className={styles.storageRangeLabel}>GB</span>
          </div>
          <CheckboxFilterList
            options={[
              { label: 'USED', value: StorageFilterOption.USED },
              { label: 'AVAILABLE', value: StorageFilterOption.AVAILABLE },
              { label: 'TOTAL', value: StorageFilterOption.TOTAL },
            ]}
            selectedValues={selection.storageMetrics}
            onChange={(storageMetrics) => onChange({ storageMetrics: storageMetrics as StorageFilterOption[] })}
          />
        </Panel>
      </Collapse>

      <div className={styles.divider} />

      <div className={styles.footer}>
        <Button type="text" className={styles.action} onClick={onClearAll}>
          CLEAR ALL
        </Button>
        <Button type="text" className={styles.action} onClick={onApply}>
          APPLY <AiFillCaretRight className={styles.applyIcon} />
        </Button>
      </div>
    </div>
  );
};
